// ShopScene.js（重构版 - 使用子模块）
import InputHelper from './modules/InputHelper.js';
import ShopUI from './modules/Shop/ShopUI.js';
import ShopLogic from './modules/Shop/ShopLogic.js';
import ShopDisplay from './modules/Shop/ShopDisplay.js';
import DebugHelper from './modules/DebugHelper.js';
import GameStateStore from './modules/GameStateStore.js';

export default class ShopScene extends Phaser.Scene {
    constructor() {
        super('ShopScene');
    }
    
    init(data) {
        // data应该包含 { shopData: NPC的buy数组, npcName: NPC名称, canSell: 是否可出售, previousSceneKey: 之前场景的key }
        this.shopData = data.shopData || [];
        this.npcName = data.npcName || '商人';
        this.canSell = data.canSell || false;
        this.playerData = GameStateStore.getPlayer();
        this.selectedItemIndex = 0;
        this.menuOptions = this.canSell ? ['购买', '出售', '离开'] : ['购买', '离开'];
        this.currentMode = 'menu'; // menu:主菜单, browse:浏览商品, buy:购买确认, sell:出售模式, sellConfirm:出售确认
        this.sellMode = false; // false:购买模式, true:出售模式
        // 保存之前场景的 key，以便关闭时恢复
        this.previousSceneKey = data?.previousSceneKey || 'WorldScene';
        
        // 初始化子模块
        this.ui = new ShopUI(this);
        this.logic = new ShopLogic(this, this.playerData);
        this.display = new ShopDisplay(this, this.ui);
    }
    
    create() {
        this.ui.createBackground(this.npcName);
        this.ui.createShopUI();
        this.ui.createMoneyDisplay(this.playerData.money);
        this.setupControls();
        this.updateDisplay();
    }
    
    setupControls() {
        this.menuInput = InputHelper.bindMenuKeyboard(this, {
            blockWhen: () => this.currentMode === 'processing',
            onUp: () => this.moveSelection(-1),
            onDown: () => this.moveSelection(1),
            onSelect: () => this.selectItem(),
            onCancel: () => {
                if (this.currentMode === 'confirm' || this.currentMode === 'sellConfirm') {
                    if (this.currentMode === 'confirm') {
                        this.currentMode = 'browse';
                    } else {
                        this.currentMode = 'sell';
                    }
                    this.selectedItemIndex = 0;
                    this.updateDisplay();
                } else if (this.currentMode === 'menu') {
                    this.exitShop();
                } else {
                    this.currentMode = 'menu';
                    this.selectedItemIndex = 0;
                    this.updateDisplay();
                }
            }
        });
    }

    destroyMenuInput() {
        if (this.menuInput) {
            this.menuInput.destroy();
            this.menuInput = null;
        }
    }
    
    moveSelection(direction) {
        // 处理中时忽略移动
        if (this.currentMode === 'processing') {
            return;
        }
        
        if (this.currentMode === 'menu') {
            // 主菜单模式
            const maxIndex = this.menuOptions.length - 1;
            this.selectedItemIndex = Phaser.Math.Wrap(
                this.selectedItemIndex + direction,
                0,
                maxIndex + 1
            );
        } else if (this.currentMode === 'browse' || this.currentMode === 'sell') {
            // 浏览模式或出售模式（包括"返回"选项）
            const itemList = this.sellMode ? this.logic.getSellableItems() : this.shopData;
            // 包括"返回"选项，所以最大索引是 itemList.length
            const maxIndex = itemList.length;
            this.selectedItemIndex = Phaser.Math.Wrap(
                this.selectedItemIndex + direction,
                0,
                maxIndex + 1
            );
        } else if (this.currentMode === 'confirm' || this.currentMode === 'sellConfirm') {
            // 确认模式：确定/取消
            this.selectedItemIndex = (this.selectedItemIndex + direction) % 2;
        }
        
        this.updateDisplay();
    }
    
    selectItem() {
        // 如果正在处理中（购买/出售执行中），忽略输入
        if (this.currentMode === 'processing') {
            return;
        }
        
        if (this.currentMode === 'menu') {
            // 主菜单选择
            const option = this.menuOptions[this.selectedItemIndex];
            if (option === '购买') {
                this.sellMode = false;
                this.currentMode = 'browse';
                this.selectedItemIndex = 0;
                this.updateDisplay();
            } else if (option === '出售') {
                this.sellMode = true;
                this.currentMode = 'sell';
                this.selectedItemIndex = 0;
                this.updateDisplay();
            } else if (option === '离开') {
                this.exitShop();
            }
        } else if (this.currentMode === 'browse') {
            // 购买模式：选择商品
            // 检查是否选择了"返回"选项（在列表末尾）
            if (this.selectedItemIndex === this.shopData.length) {
                // 选择了返回选项
                this.currentMode = 'menu';
                this.selectedItemIndex = 0;
                this.updateDisplay();
                return;
            }
            
            const selectedItem = this.shopData[this.selectedItemIndex];
            
            if (this.logic.canPurchase(selectedItem)) {
                // 进入购买确认模式
                this.confirmPurchase(selectedItem);
            } else {
                this.ui.showMessage('金钱不足！', '#ff0000');
            }
        } else if (this.currentMode === 'confirm') {
            // 购买确认：确定/取消
            if (this.selectedItemIndex === 0) {
                // 确定购买
                this.executePurchase(this.selectedItem);
            } else {
                // 取消，返回浏览
                this.currentMode = 'browse';
                this.selectedItemIndex = 0;
                this.updateDisplay();
            }
        } else if (this.currentMode === 'sell') {
            // 出售模式：选择要出售的物品
            const sellableItems = this.logic.getSellableItems();
            
            // 检查是否选择了"返回"选项（在列表末尾）
            if (this.selectedItemIndex === sellableItems.length) {
                // 选择了返回选项
                this.currentMode = 'menu';
                this.selectedItemIndex = 0;
                this.updateDisplay();
                return;
            }
            
            if (sellableItems.length === 0) {
                this.ui.showMessage('没有可出售的物品！', '#ff0000');
                return;
            }
            
            const selectedItem = sellableItems[this.selectedItemIndex];
            this.confirmSell(selectedItem);
        } else if (this.currentMode === 'sellConfirm') {
            // 出售确认：确定/取消
            if (this.selectedItemIndex === 0) {
                // 确定出售
                this.executeSell(this.selectedItem);
            } else {
                // 取消，返回出售列表
                this.currentMode = 'sell';
                this.selectedItemIndex = 0;
                this.updateDisplay();
            }
        }
    }
    
    confirmPurchase(item) {
        this.currentMode = 'confirm';
        this.selectedItem = item;
        this.selectedItemIndex = 0; // 重置为第一个选项
        
        // 更新显示，隐藏商品列表，只显示确认信息
        this.updateDisplay();
        
        this.ui.showMessage(
            `购买 ${item.name} 需要 ${item.price} G\n确定购买吗？\n(空格:确定 ESC:取消)`,
            '#ffff00'
        );
    }
    
    executePurchase(item) {
        // 立即设置为处理中状态，防止重复购买
        this.currentMode = 'processing';
        
        const result = this.logic.executePurchase(item);
        
        if (GameStateStore.getPlayer()) {
            const patch = {
                money: this.playerData.money,
                inventory: this.playerData.inventory
            };
            if (this.playerData.vehicle) {
                patch.vehicle = this.playerData.vehicle;
            }
            GameStateStore.patchPlayer(patch);
        }
        
        // 更新UI
        // 确保 newMoney 有值，如果没有则使用当前金钱
        const moneyToDisplay = result.newMoney !== undefined ? result.newMoney : this.playerData.money;
        this.ui.updateMoneyDisplay(moneyToDisplay);
        // 根据成功/失败显示不同颜色的消息
        const messageColor = result.success ? '#00ff00' : '#ff0000';
        this.ui.showMessage(result.message, messageColor);
        
        // 尝试更新世界场景的HUD（如果场景存在）
        this.updateWorldHUD();
        
        // 立即更新显示，清除确认弹窗
        this.updateDisplay();
        
        // 返回浏览模式
        this.time.delayedCall(1000, () => {
            this.currentMode = 'browse';
            this.updateDisplay();
        });
    }
    
    confirmSell(itemData) {
        this.currentMode = 'sellConfirm';
        this.selectedItem = itemData;
        this.selectedItemIndex = 0; // 重置为第一个选项（确定）
        
        const sellPrice = this.logic.calculateSellPrice(itemData.item);
        const totalPrice = sellPrice * itemData.count;
        
        this.ui.showMessage(
            `出售 ${itemData.item.name} x${itemData.count}\n获得 ${totalPrice} G\n确定出售吗？\n(空格:确定 ESC:取消)`,
            '#ffff00'
        );
    }
    
    executeSell(itemData) {
        // 立即设置为处理中状态，防止重复出售
        this.currentMode = 'processing';
        
        const result = this.logic.executeSell(itemData);
        
        if (GameStateStore.getPlayer()) {
            GameStateStore.patchPlayer({
                money: this.playerData.money,
                inventory: this.playerData.inventory
            });
        }
        
        // 更新UI
        this.ui.updateMoneyDisplay(result.newMoney);
        this.ui.showMessage(result.message, '#00ff00');
        
        // 尝试更新世界场景的HUD（如果场景存在）
        this.updateWorldHUD();
        
        // 立即更新显示，清除确认弹窗
        this.updateDisplay();
        
        // 返回出售列表模式
        this.time.delayedCall(1000, () => {
            this.currentMode = 'sell';
            this.selectedItemIndex = 0;
            this.updateDisplay();
        });
    }
    
    /**
     * 更新世界场景的HUD（如果场景存在）
     */
    updateWorldHUD() {
        const previousSceneKey = this.previousSceneKey || 'WorldScene';
        const worldScene = this.scene.get(previousSceneKey);
        if (worldScene && worldScene.worldUI && this.playerData) {
            try {
                worldScene.worldUI.updateHUD(this.playerData);
            } catch (error) {
                // 如果场景已暂停或HUD不存在，忽略错误
                DebugHelper.warn('更新世界场景HUD失败:', error);
            }
        }
    }
    
    updateDisplay() {
        // 更新模式提示
        this.ui.updateModeText(this.currentMode);
        
        if (this.currentMode === 'menu') {
            // 显示主菜单
            this.display.showMenu(
                this.menuOptions,
                this.selectedItemIndex,
                (index, action) => {
                    if (action === 'hover') {
                        this.selectedItemIndex = index;
                        this.updateDisplay();
                    } else if (action === 'click') {
                        this.selectedItemIndex = index;
                        this.selectItem();
                    }
                }
            );
        } else if (this.currentMode === 'browse') {
            // 显示商品列表
            this.display.showShopItems(
                this.shopData,
                this.selectedItemIndex,
                this.playerData.money,
                (index) => {
                    this.selectedItemIndex = index;
                    this.selectItem();
                },
                (index) => {
                    this.selectedItemIndex = index;
                    this.updateDisplay();
                }
            );
            
            // 显示选中商品详情（如果不是返回选项）
            if (this.selectedItemIndex < this.shopData.length && this.shopData[this.selectedItemIndex]) {
                const item = this.shopData[this.selectedItemIndex];
                this.display.showItemDetails(item, this.playerData.money);
            } else {
                // 选择了返回选项，清空详情
                this.ui.itemDetails.removeAll(true);
            }
        } else if (this.currentMode === 'sell') {
            // 显示可出售物品列表
            const sellableItems = this.logic.getSellableItems();
            
            this.display.showSellableItems(
                sellableItems,
                this.selectedItemIndex,
                (item) => this.logic.calculateSellPrice(item),
                (index) => {
                    this.selectedItemIndex = index;
                    this.selectItem();
                },
                (index) => {
                    this.selectedItemIndex = index;
                    this.updateDisplay();
                }
            );
            
            // 显示选中物品详情（如果不是返回选项）
            if (this.selectedItemIndex < sellableItems.length && sellableItems[this.selectedItemIndex]) {
                const itemData = sellableItems[this.selectedItemIndex];
                this.display.showSellItemDetails(
                    itemData.item,
                    itemData.count,
                    (item) => this.logic.calculateSellPrice(item)
                );
            } else {
                // 选择了返回选项，清空详情
                this.ui.itemDetails.removeAll(true);
            }
        } else if (this.currentMode === 'confirm' || this.currentMode === 'sellConfirm') {
            // 显示确认选项
            this.display.showConfirmOptions(
                this.selectedItemIndex,
                (index, action) => {
                    if (action === 'hover') {
                        this.selectedItemIndex = index;
                        this.updateDisplay();
                    } else if (action === 'click') {
                        this.selectedItemIndex = index;
                        this.selectItem();
                    }
                }
            );
        }
    }
    
    exitShop() {
        this.destroyMenuInput();
        const previousSceneKey = this.previousSceneKey || 'WorldScene';
        
        if (GameStateStore.getPlayer()) {
            const patch = {
                money: this.playerData.money,
                inventory: this.playerData.inventory
            };
            if (this.playerData.vehicle) {
                patch.vehicle = this.playerData.vehicle;
            }
            GameStateStore.patchPlayer(patch);
        }
        
        this.scene.stop();
        this.scene.resume(previousSceneKey, {
            from: 'ShopScene',
            player: this.playerData
        });
        
        // 尝试直接更新世界场景的HUD（如果场景已存在）
        const worldScene = this.scene.get(previousSceneKey);
        if (worldScene && worldScene.worldUI && this.playerData) {
            worldScene.worldUI.updateHUD(this.playerData);
        }
    }
    
    destroy() {
        this.destroyMenuInput();
        // 清理
        if (this.ui) {
            this.ui.destroy();
        }
    }
}
